import {
  UsersHeader,
  TradingUserForm,
  UsersInfo,
  AccountActivity,
  Notes,
  AddPackageDialog,
  PayoutHistory,
} from '@features/admin/users';
import { Tab } from '@features/ui';
import { TTab } from '@src/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LeelooAccounts,
  UserDetailQuery,
  UserNote,
  UserProfile,
} from '@src/types/users.ts';
import { useParams } from 'react-router-dom';
import {
  deactivateUser,
  flagUser,
  getNotes,
  getUserDetail,
  addPackageToUser,
  getAccountActivities,
  deleteUser,
  updateRithmic,
  resendWelcomeEmail,
} from '@src/services/users.ts';
import { useCountryOptions } from '@hooks/useCountryOptions.ts';
import { useStateOptions } from '@src/hooks/useStateOptions';
import { toast } from 'react-toastify';

export const UserDetailPage = () => {
  const [tabActive, setTabActive] = useState(0);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [filteredAccounts, setFilteredAccounts] = useState<LeelooAccounts[]>(
    []
  );
  const [notes, setNotes] = useState<UserNote[] | null>(null);
  const [addPackageOpen, setAddPackageOpen] = useState(false);

  const params = useParams();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();
  const { t } = useTranslation();

  const fetchAccountActivities = useCallback(
    async (id: number, userQuery?: UserDetailQuery) => {
      const res = await getAccountActivities(id, userQuery);

      setFilteredAccounts(res.data);
    },
    []
  );

  const fetUserDetail = useCallback(
    async (id: number, userQuery?: UserDetailQuery) => {
      const res = await getUserDetail(id, userQuery);

      setUser(res.data);
    },
    []
  );

  const fetchNotes = useCallback(async (id: number) => {
    const res = await getNotes(id);

    setNotes(res.data);
  }, []);

  useEffect(() => {
    if (!params.id) return;
    const id = Number(params.id);

    fetUserDetail(id, {
      show_evals: true,
      show_pas: true,
      live_only: true,
    }).then();
    fetchAccountActivities(id, {
      show_evals: true,
      show_pas: true,
      live_only: true,
    }).then();
    fetchNotes(id).then();
  }, [params.id, fetUserDetail, fetchNotes, fetchAccountActivities]);

  const handleFlag = useCallback(async () => {
    if (!params.id) return;
    const id = Number(params.id);

    await flagUser(id);
    await fetchNotes(id);
    await fetUserDetail(id, {
      show_evals: true,
      show_pas: true,
      live_only: true,
    });
  }, [fetchNotes, params?.id]);

  const handleDeactivate = useCallback(async () => {
    if (!params.id) return;
    try {
      const id = Number(params.id);

      const res = await deactivateUser(id);

      await fetUserDetail(id);

      toast.success(res?.data?.message || 'User deactivated successfully.');
    } catch {
      toast.error('Failed to deactivate user. Please try again.');
    }
  }, [fetUserDetail, params?.id]);

  const handleDelete = useCallback(async () => {
    if (!params.id) return;
    try {
      const id = Number(params.id);
      const res = await deleteUser(id);

      await fetUserDetail(id);
      toast.success(res?.data?.message || 'User deleted successfully.');
    } catch {
      toast.error('Failed to delete user. Please try again.');
    }
  }, [fetUserDetail, params?.id]);

  const handleAddPackage = useCallback(
    async (data: {
      product_id: number;
      leeloo_user_id: number;
      valid_from: string;
      valid_to: string;
      reason?: string;
      send_mail_new_account?: boolean;
    }) => {
      try {
        const res = await addPackageToUser(data);

        if (!params.id) return;
        const id = Number(params.id);

        await fetUserDetail(id, {
          show_evals: true,
          show_pas: true,
          live_only: true,
        });
        await fetchAccountActivities(id, {
          show_evals: true,
          show_pas: true,
          live_only: true,
        });

        toast.success(res?.data?.message || 'Package added successfully.');
      } catch {
        toast.error('Failed to add package. Please try again.');
      }
    },
    [fetUserDetail, params?.id]
  );

  const handleUserRithmicUpdate = useCallback(async () => {
    if (!params.id) return;
    try {
      const id = Number(params.id);

      const res = await updateRithmic(id);

      toast.success(
        res?.data?.message || 'Rithmic update triggered successfully.'
      );
    } catch {
      toast.error('Failed to trigger Rithmic update. Please try again.');
    }
  }, [params?.id]);

  const handleResendWelcomeEmail = useCallback(async () => {
    try {
      const id = Number(params.id);

      const res = await resendWelcomeEmail(id);

      toast.success(res?.data?.message || 'Welcome email resent successfully.');
    } catch {
      toast.error('Failed to resend welcome email. Please try again.');
    }
  }, [params?.id]);

  const tabs: TTab[] = useMemo(
    () => [
      {
        label: t('users.tab.accountActivity'),
        component: (
          <AccountActivity
            filteredAccounts={filteredAccounts}
            refreshUser={async (userQuery) => {
              if (!params.id) return;
              await fetchAccountActivities(Number(params.id), userQuery);
            }}
            user={user}
          />
        ),
      },
      {
        label: t('users.tab.payoutHistory'),
        component: <PayoutHistory userId={user?.id ?? null} />,
      },
      {
        label: t('users.tab.notes', { total: notes?.length }),
        component: (
          <Notes
            filterNotes={setNotes}
            notes={notes ?? []}
            refreshNotes={async () => {
              if (params.id) {
                await fetchNotes(Number(params.id));
              }
            }}
            userId={Number(params.id)}
          />
        ),
      },
    ],
    [
      t,
      user,
      notes,
      params.id,
      fetchNotes,
      filteredAccounts,
      fetchAccountActivities,
    ]
  );

  return (
    <div>
      <UsersHeader
        user={user}
        onAddPackage={() => setAddPackageOpen(true)}
        onDeactivate={handleDeactivate}
        onDelete={handleDelete}
        onFlag={handleFlag}
      />
      <div className={'flex gap-[15px] xl:flex-row flex-col'}>
        <TradingUserForm
          countryOptions={countryOptions}
          stateOptions={stateOptions}
          user={user}
        />
        <UsersInfo
          refreshUser={() => {
            if (params.id) {
              fetUserDetail(Number(params.id), {
                show_evals: true,
                show_pas: true,
                live_only: true,
              });
            }
          }}
          user={user}
          onResendWelcomeEmail={handleResendWelcomeEmail}
          onUserRithmicUpdate={handleUserRithmicUpdate}
          onUserUpdate={setUser}
        />
      </div>
      <div className={'mt-[23px] relative'}>
        <Tab
          className={'mb-5'}
          tabActive={tabActive}
          tabs={tabs}
          onChangeTab={setTabActive}
        />
      </div>
      <AddPackageDialog
        open={addPackageOpen}
        userId={user?.id ?? null}
        onOpenChange={setAddPackageOpen}
        onSave={handleAddPackage}
      />
    </div>
  );
};
